import { useState } from 'react'
import { Plus, Trash2, Edit2, CheckCircle, AlertCircle, Copy, Eye, EyeOff, Save, X } from 'lucide-react'

export default function APIIntegrationPage() {
  const [integrations, setIntegrations] = useState([
    { id: 1, service: 'Facebook API', category: 'Social Media', apiKey: 'fb_key_****', status: 'Connected', dateAdded: '2024-02-10', webhook: 'https://api.cybertection.com/webhook/facebook' },
    { id: 2, service: 'Twitter/X API', category: 'Social Media', apiKey: 'tw_key_****', status: 'Connected', dateAdded: '2024-02-10', webhook: 'https://api.cybertection.com/webhook/twitter' },
    { id: 3, service: 'OpenAI GPT-4', category: 'AI Services', apiKey: 'sk_****', status: 'Connected', dateAdded: '2024-02-12', webhook: null },
    { id: 4, service: 'Google Analytics', category: 'Analytics', apiKey: 'ga_key_****', status: 'Connected', dateAdded: '2024-02-15', webhook: null },
  ])

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({
    service: '',
    category: '',
    apiKey: '',
    apiSecret: '',
    webhook: '',
  })
  const [visibleKeys, setVisibleKeys] = useState({})

  const categories = ['Social Media', 'AI Services', 'Analytics', 'Email Services', 'Payment Gateway', 'Cloud Storage', 'Communication', 'Custom Webhook']

  const handleAddIntegration = () => {
    if (formData.service && formData.category && formData.apiKey) {
      if (editingId) {
        setIntegrations(integrations.map(i =>
          i.id === editingId
            ? { ...i, service: formData.service, category: formData.category, apiKey: formData.apiKey, webhook: formData.webhook }
            : i
        ))
        setEditingId(null)
      } else {
        setIntegrations([...integrations, {
          id: Date.now(),
          service: formData.service,
          category: formData.category,
          apiKey: formData.apiKey,
          status: 'Connected',
          dateAdded: new Date().toLocaleDateString(),
          webhook: formData.webhook || null,
        }])
      }
      setFormData({ service: '', category: '', apiKey: '', apiSecret: '', webhook: '' })
      setShowAddForm(false)
    }
  }

  const handleEditIntegration = (integration) => {
    setFormData({
      service: integration.service,
      category: integration.category,
      apiKey: integration.apiKey,
      apiSecret: '',
      webhook: integration.webhook || '',
    })
    setEditingId(integration.id)
    setShowAddForm(true)
  }

  const handleDeleteIntegration = (id) => {
    setIntegrations(integrations.filter(i => i.id !== id))
  }

  const toggleKeyVisibility = (id) => {
    setVisibleKeys(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert('API Key copied to clipboard!')
  }

  const getCategoryColor = (category) => {
    const colors = {
      'Social Media': 'from-pink-600/20 to-purple-600/20 border-pink-500/30',
      'AI Services': 'from-blue-600/20 to-cyan-600/20 border-blue-500/30',
      'Analytics': 'from-green-600/20 to-emerald-600/20 border-green-500/30',
      'Email Services': 'from-orange-600/20 to-red-600/20 border-orange-500/30',
      'Payment Gateway': 'from-yellow-600/20 to-orange-600/20 border-yellow-500/30',
      'Cloud Storage': 'from-purple-600/20 to-pink-600/20 border-purple-500/30',
      'Communication': 'from-blue-600/20 to-purple-600/20 border-blue-500/30',
      'Custom Webhook': 'from-slate-600/20 to-slate-600/20 border-slate-500/30',
    }
    return colors[category] || 'from-blue-600/20 to-cyan-600/20 border-blue-500/30'
  }

  const integrationsPerCategory = categories.map(cat => ({
    category: cat,
    count: integrations.filter(i => i.category === cat).length,
    items: integrations.filter(i => i.category === cat)
  }))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">API Integration Manager</h1>
        <p className="text-blue-200">Connect all your services, APIs, and webhooks in one place</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Connected</p>
          <p className="text-3xl font-bold text-blue-400">{integrations.length}</p>
          <p className="text-xs text-blue-300 mt-2">API integrations</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Active Services</p>
          <p className="text-3xl font-bold text-green-400">{integrations.filter(i => i.status === 'Connected').length}</p>
          <p className="text-xs text-green-300 mt-2">Running smoothly</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Categories</p>
          <p className="text-3xl font-bold text-purple-400">{integrationsPerCategory.filter(c => c.count > 0).length}</p>
          <p className="text-xs text-purple-300 mt-2">Service types in use</p>
        </div>

        <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
          <p className="text-orange-300/70 text-sm mb-1">Webhooks</p>
          <p className="text-3xl font-bold text-orange-400">{integrations.filter(i => i.webhook).length}</p>
          <p className="text-xs text-orange-300 mt-2">Active webhooks</p>
        </div>
      </div>

      {/* Add Integration Form */}
      {showAddForm && (
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{editingId ? 'Edit Integration' : 'Add New Integration'}</h2>
            <button
              onClick={() => {
                setShowAddForm(false)
                setEditingId(null)
                setFormData({ service: '', category: '', apiKey: '', apiSecret: '', webhook: '' })
              }}
              className="p-2 text-blue-300 hover:bg-blue-600/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-blue-300 font-semibold text-sm mb-2 block">Service Name</label>
              <input
                type="text"
                value={formData.service}
                onChange={(e) => setFormData({...formData, service: e.target.value})}
                placeholder="e.g., Facebook API, OpenAI GPT-4"
                className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-blue-300 font-semibold text-sm mb-2 block">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white focus:outline-none focus:border-blue-500/60"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-blue-300 font-semibold text-sm mb-2 block">API Key/Token</label>
                <input
                  type="password"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({...formData, apiKey: e.target.value})}
                  placeholder="Enter your API key"
                  className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
                />
              </div>
            </div>

            <div>
              <label className="text-blue-300 font-semibold text-sm mb-2 block">Webhook URL (Optional)</label>
              <input
                type="url"
                value={formData.webhook}
                onChange={(e) => setFormData({...formData, webhook: e.target.value})}
                placeholder="https://api.example.com/webhook"
                className="w-full px-4 py-2 bg-slate-800/50 border border-blue-500/30 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAddIntegration}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all flex items-center justify-center space-x-2"
              >
                <Save size={18} />
                <span>{editingId ? 'Update Integration' : 'Add Integration'}</span>
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false)
                  setEditingId(null)
                  setFormData({ service: '', category: '', apiKey: '', apiSecret: '', webhook: '' })
                }}
                className="flex-1 px-4 py-2 bg-slate-600/30 text-slate-300 rounded-lg font-semibold hover:bg-slate-600/50 transition-all flex items-center justify-center space-x-2"
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-blue-500/50 transition-all flex items-center justify-center space-x-2"
        >
          <Plus size={20} />
          <span>Add New Integration</span>
        </button>
      )}

      {/* Integrations by Category */}
      <div className="space-y-6">
        {integrationsPerCategory.map((group) => (
          group.count > 0 && (
            <div key={group.category}>
              <h2 className="text-lg font-bold text-white mb-3">{group.category} ({group.count})</h2>
              <div className="space-y-3">
                {group.items.map((integration) => (
                  <div key={integration.id} className={`bg-gradient-to-br ${getCategoryColor(integration.category)} rounded-lg border p-4`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <p className="font-bold text-white text-lg">{integration.service}</p>
                          <span className="px-3 py-1 bg-green-500/30 text-green-300 text-xs font-bold rounded-full flex items-center space-x-1">
                            <CheckCircle size={12} />
                            <span>{integration.status}</span>
                          </span>
                        </div>
                        <p className="text-sm text-blue-300/70">Added: {integration.dateAdded}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditIntegration(integration)}
                          className="p-2 text-blue-300 hover:bg-blue-600/20 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteIntegration(integration.id)}
                          className="p-2 text-red-300 hover:bg-red-600/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="bg-slate-800/30 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-blue-300 text-sm">API Key:</span>
                        <div className="flex items-center space-x-2">
                          <code className="text-xs text-white font-mono bg-slate-900/50 px-2 py-1 rounded">
                            {visibleKeys[integration.id] ? integration.apiKey : integration.apiKey}
                          </code>
                          <button
                            onClick={() => toggleKeyVisibility(integration.id)}
                            className="text-blue-300 hover:text-blue-200 transition-colors"
                          >
                            {visibleKeys[integration.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(integration.apiKey)}
                            className="text-blue-300 hover:text-blue-200 transition-colors"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>

                      {integration.webhook && (
                        <div className="flex items-start justify-between">
                          <span className="text-blue-300 text-sm">Webhook:</span>
                          <div className="flex items-center space-x-2 flex-1 ml-4">
                            <code className="text-xs text-white font-mono bg-slate-900/50 px-2 py-1 rounded flex-1 truncate">
                              {integration.webhook}
                            </code>
                            <button
                              onClick={() => copyToClipboard(integration.webhook)}
                              className="text-blue-300 hover:text-blue-200 transition-colors flex-shrink-0"
                            >
                              <Copy size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      {integrations.length === 0 && !showAddForm && (
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-12 text-center">
          <AlertCircle size={48} className="text-blue-300 mx-auto mb-4 opacity-50" />
          <p className="text-blue-200 text-lg">No integrations yet</p>
          <p className="text-blue-300/70 mt-2">Add your first API integration to get started</p>
        </div>
      )}

      {/* Info Section */}
      <div className="bg-blue-500/10 rounded-lg border border-blue-500/30 p-6">
        <h3 className="text-lg font-bold text-blue-300 mb-3">🔒 Security & Best Practices</h3>
        <ul className="space-y-2 text-sm text-blue-200">
          <li>✓ All API keys are encrypted and stored securely</li>
          <li>✓ Never share your API keys with anyone</li>
          <li>✓ Rotate your API keys regularly for enhanced security</li>
          <li>✓ Use environment-specific API keys when possible</li>
          <li>✓ Monitor webhook activity in your service logs</li>
        </ul>
      </div>
    </div>
  )
}
